import { css, StyleDeclarationValue, StyleSheet } from 'aphrodite';
import { useCallback, useMemo } from 'react';
import { CustomPageType } from '../../../extViewmodels/sdk/models/CustomPageStyle';
import { useCustomPageStyle } from '../../hooks/useCustomPageStyle';
import { styleSheet } from './styles';

interface IProps {
	cardStyles?: StyleDeclarationValue[];
	className?: string;
	contentStyles?: StyleDeclarationValue[];
	styles?: StyleDeclarationValue[];
	shortCode?: string;
	children: React.ReactNode;
}

export const MeetingContainer: React.FC<IProps> = ({
	cardStyles = [],
	children,
	className = '',
	contentStyles = [],
	styles = [],
	shortCode,
}) => {
	const {
		data: customHeaderData,
		hasCustomContent: hasCustomHeader,
		inIframe,
	} = useCustomPageStyle(shortCode, CustomPageType.Header);
	const { data: customFooterData, hasCustomContent: hasCustomFooter } = useCustomPageStyle(
		shortCode,
		CustomPageType.Footer
	);

	const dynamicContentStyles = useMemo(() => {
		const borderRadiusStyles: any = {};

		if (hasCustomHeader) {
			borderRadiusStyles.borderTopLeftRadius = 0;
			borderRadiusStyles.borderTopRightRadius = 0;
		}

		if (hasCustomFooter) {
			borderRadiusStyles.borderBottomLeftRadius = 0;
			borderRadiusStyles.borderBottomRightRadius = 0;
		}

		// Only create styles if there are actual style properties
		if (Object.keys(borderRadiusStyles).length > 0) {
			return StyleSheet.create({ dynamic: borderRadiusStyles }).dynamic;
		}
		return null;
	}, [hasCustomHeader, hasCustomFooter]);

	const renderCustomContent = useCallback((html: string, isHeader = false) => {
		const classNameStyle = isHeader ? css(styleSheet.customHeader) : css(styleSheet.customFooter);
		return <div className={classNameStyle} dangerouslySetInnerHTML={{ __html: html }} />;
	}, []);

	const renderHeader = useCallback(() => {
		// Don't show header if in iframe
		if (inIframe) {
			return null;
		}

		if (customHeaderData?.html) {
			return renderCustomContent(customHeaderData.html, true);
		}

		// No default header for meeting scheduler
		return null;
	}, [customHeaderData?.html, inIframe, renderCustomContent]);

	const renderFooter = useCallback(() => {
		// Don't show footer if in iframe
		if (inIframe) {
			return null;
		}

		if (customFooterData?.html) {
			return renderCustomContent(customFooterData.html, false);
		}

		return null;
	}, [customFooterData?.html, inIframe, renderCustomContent]);

	return (
		<div className={css(styleSheet.fullHeightWrapper)}>
			{renderHeader()}
			<div className={`meeting-container ${className} ${css(styleSheet.container, ...(styles || []))}`}>
				<div className={css(styleSheet.content, ...(cardStyles || []))}>
					<div className={css(styleSheet.body, ...[dynamicContentStyles, ...(contentStyles || [])].filter(Boolean))}>
						{children}
					</div>
				</div>
			</div>
			<div className={css(styleSheet.footer)}>{renderFooter()}</div>
		</div>
	);
};
